import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Select, Space } from 'antd';
import { getCropNamesApi } from '../../api/crops';
import { CROP_NAME_IMAGE } from '../../utils/constants';

// Crop library entries repeat once per language, and some crops (e.g. Marigold)
// exist only in a regional language. Group by common_id so every crop shows up
// once: labelled in English when there is an English row, otherwise in the first
// language it has, with the other language names alongside.
function groupByCommonId(list) {
  const groups = new Map();
  list.forEach((c) => {
    if (c.common_id == null || !c.name) return;
    const g = groups.get(c.common_id) || { common_id: c.common_id, image: null, names: [], enName: null };
    if (!g.image && c.image) g.image = c.image;
    if (c.language === 'en' && !g.enName) g.enName = c.name;
    if (!g.names.includes(c.name)) g.names.push(c.name);
    groups.set(c.common_id, g);
  });
  return Array.from(groups.values()).map((g) => {
    const name = g.enName || g.names[0];
    return { ...g, name, otherNames: g.names.filter((n) => n !== name) };
  });
}

// Searchable crop picker whose value is the crop's common_id.
// onChange(commonId, crop) — crop is the crop_names row (name, image, common_id).
export default function CropCommonIdSelect({ value, onChange, style }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const searchTimer = useRef(null);

  const load = useCallback(async (text = '') => {
    setLoading(true);
    try {
      // No lang: search every language's names, so "ગલગોટા" and "Marigold" both find it.
      const res = await getCropNamesApi({ crop: text });
      const raw = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setOptions(groupByCommonId(raw));
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSearch = (text) => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => load(text), 300);
  };

  return (
    <Select
      showSearch
      value={value}
      onChange={(v) => onChange(v, options.find((c) => c.common_id === v) || null)}
      onSearch={onSearch}
      filterOption={false}
      loading={loading}
      placeholder="Select crop"
      style={{ width: 240, ...style }}
      options={options.map((c) => ({
        value: c.common_id,
        label: (
          <Space size={8}>
            {c.image && (
              <img src={CROP_NAME_IMAGE(c.image)} alt="" style={{ width: 20, height: 20, borderRadius: 3, objectFit: 'cover' }} />
            )}
            <span>{c.name}</span>
            {c.otherNames.length > 0 && (
              <span style={{ color: '#9ca3af', fontSize: 12 }}>{c.otherNames.slice(0, 3).join(' · ')}</span>
            )}
          </Space>
        ),
      }))}
    />
  );
}
